export interface DerivedSeed {
  readonly outcomes: number;
  readonly skew: number;
  readonly axis: 'radial' | 'horizontal';
  readonly phase: number;
  readonly paletteRoll: number;
  readonly turbulence: number;
  readonly hash: number;
}

export interface HotSpot {
  readonly x: number;
  readonly y: number;
  readonly rx: number;
  readonly ry: number;
  readonly rot: number;
  readonly amp: number;
}

export interface Site {
  readonly x: number;
  readonly y: number;
  readonly w: number;
}

export interface SourcePoint {
  readonly x: number;
  readonly y: number;
  readonly rot: number;
}

export type MatrixBias = 'scatter' | 'gradient' | 'radial' | 'diagonal';

export type SourceLayout = 'center' | 'pair' | 'row' | 'corners' | 'ring' | 'scatter';

export interface DensityFieldConfig {
  readonly width: number;
  readonly height: number;
  readonly mode: string;
  readonly seed: number;
  readonly derived: DerivedSeed | null;
  readonly srcLayout: SourceLayout;
  readonly srcCount: number;
  readonly srcSize: number;
  readonly srcElong: number;
  readonly srcFalloff: number;
  readonly srcDivide: number;
  readonly convN: number;
  readonly convGap: number;
  readonly convRot: number;
  readonly convTwist: number;
  readonly kaleN: number;
  readonly kaleRot: number;
  readonly matrixCell: number;
  readonly matrixFill: number;
  readonly matrixLevels: number;
  readonly matrixBias: MatrixBias;
  /** Per-render cache for seeded site/hot/source lists. */
  _cache: Record<string, HotSpot[] | Site[] | SourcePoint[]>;
}

export const LEGACY_GENERATIVE_VARIANTS = ['waves', 'rings', 'stripes', 'noise', 'bloom'] as const;

export type LegacyGenerativeVariant = (typeof LEGACY_GENERATIVE_VARIANTS)[number];

/** All dot-studio pattern modes supported by the density field. */
export const DOT_STUDIO_MODES = [
  'breakthrough',
  'horizon',
  'ridge',
  'marketLife',
  'halfLife',
  'crowd',
  'current',
  'eddy',
  'confluence',
  'cascade',
  'braid',
  'ripple',
  'resonance',
  'beat',
  'standing',
  'moire',
  'edges',
  'territory',
  'crystal',
  'stipple',
  'converge',
  'radiate',
  'pinwheel',
  'arrows',
  'surge',
  'crossfade',
  'heatmap',
  'polarization',
  'defection',
  'split',
] as const;

export type DotStudioMode = (typeof DOT_STUDIO_MODES)[number];

/** Curated hero-cover presets shown in the profile cover picker. */
export const HERO_COVER_VARIANTS: readonly DotStudioMode[] = [
  'breakthrough',
  'horizon',
  'ridge',
  'marketLife',
  'current',
  'eddy',
  'ripple',
  'resonance',
  'edges',
  'territory',
  'converge',
  'pinwheel',
];

export type GenerativeVariant = LegacyGenerativeVariant | DotStudioMode;

export const ALL_GENERATIVE_VARIANTS: readonly GenerativeVariant[] = [
  ...LEGACY_GENERATIVE_VARIANTS,
  ...DOT_STUDIO_MODES,
];

export function isDotStudioMode(variant: GenerativeVariant): variant is DotStudioMode {
  return (DOT_STUDIO_MODES as readonly string[]).includes(variant);
}

export function isLegacyGenerativeVariant(
  variant: GenerativeVariant,
): variant is LegacyGenerativeVariant {
  return (LEGACY_GENERATIVE_VARIANTS as readonly string[]).includes(variant);
}
